// pages/StandingsPage.jsx
import React from "react";
import { Alert, Box, Tabs, Tab } from "@mui/material";
import PageShell from "../components/PageShell";
import StandingsSection from "../components/StandingsSection";
import ResultHistory from "../components/ResultHistory";
import PodiumDisplay from "../components/PodiumDisplay";
import { AC_EVO_FILES } from "../results/files.config";
import { buildAceStandings } from "../utils/aceResults";

function getStandingsPoints(driver) {
  return driver?.current_points ?? driver?.points ?? 0;
}

function getTopDrivers(driverRows = []) {
  return [...driverRows]
    .sort((a, b) => getStandingsPoints(b) - getStandingsPoints(a))
    .slice(0, 3);
}

/**
 * Standings page with ACC, Assetto Corsa EVO, and AC tabs.
 * Props: { data }
 */
export default function StandingsPage({ data }) {
  // data.standings = { AC: {...}, ACC: {...}, AC_EVO: {...} }
  const ac = data?.standings?.AC ?? {};
  const acc = data?.standings?.ACC ?? {};
  const acEvo = data?.standings?.AC_EVO ?? data?.standings?.ACEVO ?? {};
  const drivers = React.useMemo(() => data?.drivers?.list ?? [], [data?.drivers?.list]);
  const [aceSessions, setAceSessions] = React.useState([]);
  const [aceLoading, setAceLoading] = React.useState(true);
  const [aceError, setAceError] = React.useState("");

  React.useEffect(() => {
    let active = true;
    setAceLoading(true);
    Promise.all(
      AC_EVO_FILES.map(async (file) => {
        const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${file}` : `/${file}`;
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) throw new Error(`${file}: HTTP ${response.status}`);
        return response.json();
      })
    )
      .then((sessions) => {
        if (!active) return;
        setAceSessions(sessions);
        setAceError("");
      })
      .catch((error) => {
        if (active) setAceError(error.message || String(error));
      })
      .finally(() => {
        if (active) setAceLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const acIntro =
    ac.intro ??
    data?.standingsIntro ??
    data?.standings?.intro ??
    "Driver & Team points, drop rounds, class splits, and stats.";
  const accIntro =
    acc.intro ??
    data?.standingsIntro ??
    data?.standings?.intro ??
    "Driver & Team points, drop rounds, class splits, and stats.";
  const acEvoIntro =
    acEvo.intro ??
    data?.standingsIntro ??
    data?.standings?.intro ??
    "Driver & Team points, drop rounds, class splits, and stats.";

  const acDrop = ac.dropRounds ?? data?.standings?.dropRounds;
  const accDrop = acc.dropRounds ?? data?.standings?.dropRounds;
  const acEvoDrop = acEvo.dropRounds ?? data?.standings?.dropRounds;

  const acDrivers = React.useMemo(() => (Array.isArray(ac.drivers) ? ac.drivers : []), [ac.drivers]);
  const acTeams = Array.isArray(ac.teams) ? ac.teams : [];

  const accDrivers = React.useMemo(() => (Array.isArray(acc.drivers) ? acc.drivers : []), [acc.drivers]);
  const accTeams = Array.isArray(acc.teams) ? acc.teams : [];
  const aceComputed = React.useMemo(
    () => buildAceStandings(aceSessions, drivers, Number(acEvoDrop) || 0),
    [aceSessions, drivers, acEvoDrop]
  );
  const acEvoDrivers = React.useMemo(
    () => aceComputed.drivers.length
      ? aceComputed.drivers
      : Array.isArray(acEvo.drivers) ? acEvo.drivers : [],
    [aceComputed.drivers, acEvo.drivers]
  );
  const acEvoTeams = aceComputed.teams.length
    ? aceComputed.teams
    : Array.isArray(acEvo.teams) ? acEvo.teams : [];
  const accTopDrivers = React.useMemo(() => getTopDrivers(accDrivers), [accDrivers]);
  const acTopDrivers = React.useMemo(() => getTopDrivers(acDrivers), [acDrivers]);
  const acEvoTopDrivers = React.useMemo(() => getTopDrivers(acEvoDrivers), [acEvoDrivers]);

  const [tab, setTab] = React.useState(0);

  const selectedDrop = tab === 0 ? accDrop : tab === 1 ? acEvoDrop : acDrop;
  const chips =
    typeof selectedDrop === "number"
      ? [`Drop Rounds: ${selectedDrop}`]
      : undefined;

  return (
    <PageShell title="Standings" chips={chips} bannerImage="podium.png">
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          aria-label="Standings game tabs"
        >
          <Tab label="Assetto Corsa Competizione" id="acc-tab" aria-controls="acc-panel" />
          <Tab label="Assetto Corsa EVO" id="ac-evo-tab" aria-controls="ac-evo-panel" />
          <Tab label="Assetto Corsa" id="ac-tab" aria-controls="ac-panel" />
        </Tabs>
      </Box>

      {/* Assetto Corsa EVO */}
      <Box
        role="tabpanel"
        id="ac-evo-panel"
        aria-labelledby="ac-evo-tab"
        hidden={tab !== 1}
        sx={{ pt: 1 }}
      >
        {tab === 1 && (
          <>
            {aceError && <Alert severity="error" sx={{ mb: 2 }}>Could not load EVO results: {aceError}</Alert>}
            {aceLoading ? (
              <Alert severity="info" sx={{ mb: 2 }}>Loading Assetto Corsa EVO standings…</Alert>
            ) : acEvoDrivers.length > 0 ? (
              <PodiumDisplay topDrivers={acEvoTopDrivers} drivers={drivers} />
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Assetto Corsa EVO standings will appear here once the championship begins.
              </Alert>
            )}
            <ResultHistory data={data} sim="AC_EVO" />
            <StandingsSection
              intro={acEvoIntro}
              drop={acEvoDrop}
              drivers={drivers}
              driverRows={acEvoDrivers}
              teamRows={acEvoTeams}
            />
          </>
        )}
      </Box>

      {/* ACC */}
      <Box role="tabpanel" id="acc-panel" aria-labelledby="acc-tab" hidden={tab !== 0} sx={{ pt: 1 }}>
        {tab === 0 && (
          <>
            <PodiumDisplay topDrivers={accTopDrivers} drivers={drivers} />
            <ResultHistory data={data} sim="ACC" />
            <StandingsSection
              intro={accIntro}
              drop={accDrop}
              drivers={drivers}
              driverRows={accDrivers}
              teamRows={accTeams}
            />
          </>
        )}
      </Box>

      {/* AC */}
      <Box role="tabpanel" id="ac-panel" aria-labelledby="ac-tab" hidden={tab !== 2} sx={{ pt: 1 }}>
        {tab === 2 && (
          <>
            <PodiumDisplay topDrivers={acTopDrivers} drivers={drivers} />
            <ResultHistory data={data} sim="AC" />
            <StandingsSection
              intro={acIntro}
              drop={acDrop}
              drivers={drivers}
              driverRows={acDrivers}
              teamRows={acTeams}
            />
          </>
        )}
      </Box>
    </PageShell>
  );
}
